// Mirrored into src/utils for CRA and Functions' separate deployment roots.
// A regression test enforces identical detector implementations.
/** Classify monochrome artwork and find alpha or connected ink bounds.
 * @param {object} image RGBA pixels and dimensions.
 * @param {object} options Configurable contrast thresholds (0-255 luminance).
 * @return {object} Bounds, diagnostics and optional foreground mask.
 */
export function detectArtworkContent(image, options = {}) {
  const {data, width, height} = image;
  const seedContrast = options.seedContrast ?? 24;
  const edgeContrast = options.edgeContrast ?? 1;
  const maxEdgeDistance = options.maxEdgeDistance ?? 3;
  const count = width * height;
  const luma = (i) => 0.2126 * data[i * 4] +
    0.7152 * data[i * 4 + 1] + 0.0722 * data[i * 4 + 2];
  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  let visible = 0;
  let white = 0;
  for (let i = 0; i < count; i++) {
    if (!data[i * 4 + 3]) continue;
    visible++;
    if (luma(i) >= 200) white++;
    const x = i % width;
    const y = Math.floor(i / width);
    left = Math.min(left, x); right = Math.max(right, x);
    top = Math.min(top, y); bottom = Math.max(bottom, y);
  }
  const alphaBounds = visible ? {left, top,
    width: right - left + 1, height: bottom - top + 1} : null;
  const transparentFraction = 1 - visible / count;
  const coverage = (bounds, prefix) => ({
    [`${prefix}CoverageWidthPercent`]: bounds ? 100 * bounds.width / width : 0,
    [`${prefix}CoverageHeightPercent`]: bounds ?
      100 * bounds.height / height : 0,
    [`${prefix}CoverageAreaPercent`]: bounds ?
      100 * bounds.width * bounds.height / count : 0,
  });
  const suspiciousAlpha = alphaBounds &&
    ((alphaBounds.width / width > 0.9 &&
        alphaBounds.height / height > 0.85) ||
      alphaBounds.width * alphaBounds.height / count > 0.8);
  const meaningfulTransparency = transparentFraction >= 0.02 &&
    visible > 0 && white / visible < 0.65;
  const info = {alphaBounds, inkBounds: null, meaningfulTransparency,
    transparentFraction, ...coverage(alphaBounds, "alpha"),
    ...coverage(null, "ink")};
  const invalid = (reason) => ({...info, method: "invalid", bounds: null,
    reason, selectionReason: reason});
  const alphaFallback = (reason) => meaningfulTransparency && !suspiciousAlpha ?
    {...info, method: "alpha", bounds: alphaBounds,
      backgroundKind: "transparent", selectionReason: reason} : invalid(reason);
  if (!visible) return invalid("No visible artwork.");
  // Sample the alpha rectangle's perimeter too, so a transparent outer rim
  // cannot hide an opaque interior background from the estimator.
  const samples = [];
  const sample = (x, y) => {
    const i = y * width + x;
    if (data[i * 4 + 3] >= 240) samples.push(luma(i));
  };
  for (let x = left; x <= right; x++) {
    sample(x, top); sample(x, bottom);
  }
  for (let y = top + 1; y < bottom; y++) {
    sample(left, y); sample(right, y);
  }
  samples.sort((a, b) => a - b);
  let background = samples[Math.floor(samples.length / 2)];
  if (!samples.length || background < 200 ||
      samples.filter((v) => Math.abs(v - background) <= 12).length /
        samples.length < 0.8) {
    // A genuinely transparent contour can have dark perimeter samples.
    // Composite against white to compute an independent ink candidate.
    if (!meaningfulTransparency) {
      return invalid("Ambiguous background; use monochrome art on white.");
    }
    background = 255;
  }
  const contrast = (i) => (background - luma(i)) * data[i * 4 + 3] / 255;
  const mask = new Uint8Array(count);
  const queue = new Uint32Array(count);
  let tail = 0;
  for (let i = 0; i < count; i++) {
    if (data[i * 4 + 3] && contrast(i) >= seedContrast) {
      mask[i] = 1; queue[tail++] = i;
    }
  }
  if (!tail) return alphaFallback("No visible artwork: no foreground ink.");
  if (!meaningfulTransparency && tail / visible > 0.45) {
    return invalid("Predominantly dark artwork is not a contour master.");
  }
  // Retain very light antialiasing connected to dark contours, while avoiding
  // treating isolated near-white background noise as a new bounding edge.
  const visit = (i, distance) => {
    if (!mask[i] && data[i * 4 + 3] &&
        distance <= maxEdgeDistance + 1 &&
        contrast(i) >= edgeContrast - 0.001) {
      mask[i] = distance; queue[tail++] = i;
    }
  };
  left = width; top = height; right = -1; bottom = -1;
  for (let head = 0; head < tail; head++) {
    const i = queue[head];
    const x = i % width;
    const y = Math.floor(i / width);
    left = Math.min(left, x); right = Math.max(right, x);
    top = Math.min(top, y); bottom = Math.max(bottom, y);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (x + dx >= 0 && x + dx < width &&
            y + dy >= 0 && y + dy < height) {
          visit(i + dy * width + dx, mask[i] + 1);
        }
      }
    }
  }
  const inkBounds = {left, top, width: right - left + 1,
    height: bottom - top + 1};
  const tighter = inkBounds.width * inkBounds.height <
    alphaBounds.width * alphaBounds.height * 0.75;
  const selectInk = !meaningfulTransparency || (suspiciousAlpha && tighter);
  const selectionReason = selectInk ?
    "ink preferred: alpha covers " +
      `${info.alphaCoverageWidthPercent.toFixed(1)}% ` +
      `width and ${info.alphaCoverageHeightPercent.toFixed(1)}% height` :
    "alpha preferred: transparent foreground; ink is not materially better";
  return {...info, method: selectInk ? "ink" : "alpha",
    bounds: selectInk ? inkBounds : alphaBounds, inkBounds, mask,
    ...coverage(inkBounds, "ink"), selectionReason,
    meaningfulTransparency: !selectInk && meaningfulTransparency,
    backgroundLuminance: background,
    backgroundKind: selectInk ? "opaque-light" : "transparent",
    seedContrast, edgeContrast};
}
